import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { RouteEntity, RouteStop } from '../types';
import './WhatsAppTracking.css';
import { TILE_URL, TILE_ATTRIBUTION, TILE_MAX_ZOOM } from '../utils/mapTiles';

declare const L: any;

const API_BASE = process.env.REACT_APP_API_URL || '';
const POLL_INTERVAL_MS = 15_000;

interface TrackingLocation {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  heading: number | null;
  speed: number | null;
  updatedAt: string;
}

interface TrackingRoute extends RouteEntity {
  location: TrackingLocation | null;
}

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const radius = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const value = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return radius * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

function formatTime(value: string | null | undefined) {
  return value ? new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—';
}

export default function PublicTracking() {
  const { code, id } = useParams<{ code?: string; id?: string }>();
  const routeParam = id || code || '';
  const [route, setRoute] = useState<TrackingRoute | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  const { routeId, stop } = useMemo(() => {
    const match = routeParam.match(/^(.+)-(\d+)$/);
    return { routeId: match ? match[1] : routeParam, stop: match ? match[2] : '1' };
  }, [routeParam]);

  useEffect(() => {
    if (!routeId) {
      setLoading(false);
      setLoadError(true);
      return;
    }
    let disposed = false;

    const loadTracking = async () => {
      try {
        const { data } = await axios.get<TrackingRoute>(`${API_BASE}/tracking/${routeId}`, { params: { stop } });
        if (!disposed) {
          setRoute(data);
          setLoadError(false);
        }
      } catch {
        if (!disposed) {
          setRoute(null);
          setLoadError(true);
        }
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    loadTracking();
    const interval = window.setInterval(loadTracking, POLL_INTERVAL_MS);
    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, [routeId, stop]);

  const activeStop: RouteStop | null = route?.stops?.[0] || null;
  const destination = activeStop ? { lat: activeStop.deliveryPoint.latitude, lng: activeStop.deliveryPoint.longitude } : null;
  const location = route?.location || null;
  const distanceKm = location && destination ? haversineKm({ lat: location.latitude, lng: location.longitude }, destination) : null;
  const etaMinutes = distanceKm === null ? null : Math.max(1, Math.round((distanceKm / 28) * 60));
  const deliveryDone = route?.status === 'concluida' || activeStop?.status === 'concluida';

  useEffect(() => {
    if (!mapContainerRef.current || !destination || typeof L === 'undefined') return;
    if (mapRef.current) mapRef.current.remove();

    const map = L.map(mapContainerRef.current, { zoomControl: true, attributionControl: false }).setView([destination.lat, destination.lng], 14);
    L.tileLayer(TILE_URL, { maxZoom: TILE_MAX_ZOOM, attribution: TILE_ATTRIBUTION }).addTo(map);

    const destinationIcon = L.divIcon({
      html: '<div style="background:#0f4c5c;color:#fff;width:36px;height:36px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:grid;place-items:center;border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,.3)"><span style="transform:rotate(45deg)">⌂</span></div>',
      className: '', iconSize: [36, 36], iconAnchor: [18, 36]
    });
    L.marker([destination.lat, destination.lng], { icon: destinationIcon }).addTo(map).bindPopup('<b>Seu endereço de entrega</b>');

    if (location) {
      const driverIcon = L.divIcon({
        html: '<div style="position:relative;width:32px;height:32px"><div style="position:absolute;inset:-8px;background:rgba(42,124,229,.3);border-radius:50%;animation:gpsPulse 1.6s ease-out infinite"></div><div style="position:absolute;inset:0;background:#1677c8;border-radius:50%;border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,.3);display:grid;place-items:center;color:#fff">🚚</div></div>',
        className: '', iconSize: [32, 32], iconAnchor: [16, 16]
      });
      L.marker([location.latitude, location.longitude], { icon: driverIcon, zIndexOffset: 1000 }).addTo(map).bindPopup('<b>Veículo em rota</b>');
      L.polyline([[location.latitude, location.longitude], [destination.lat, destination.lng]], { color: '#0f4c5c', weight: 5, opacity: 0.8, dashArray: '8 6' }).addTo(map);
      map.fitBounds(L.latLngBounds([[location.latitude, location.longitude], [destination.lat, destination.lng]]).pad(0.3));
    }

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [destination?.lat, destination?.lng, location?.latitude, location?.longitude]);

  if (loading) return <div className="track-shell" style={{ display: 'grid', placeItems: 'center' }}><p>Carregando rastreamento...</p></div>;
  if (loadError || !route || !activeStop) return <div className="track-shell"><div className="track-container"><div className="track-not-found"><h2>Link de rastreamento inválido</h2><p>Verifique o link recebido ou entre em contato com a transportadora.</p></div></div></div>;

  const driverName = route.vehicle?.driverName || 'Motorista da rota';
  const vehicleName = route.vehicle?.name || 'Veículo';
  const stepLabel = deliveryDone ? 'Entregue' : route.status === 'em_andamento' ? 'A caminho de você' : 'Rota preparada';

  return (
    <div className="track-shell">
      <div className="track-container">
        <div className="track-header"><div className="brand-mini">⌖</div><div className="info"><div className="cname">{route.company?.name || 'OtimizaRota'}</div><div className="order">Rastreamento da entrega</div></div></div>
        <div className="track-status-hero">
          <div className="track-status-badge"><span className="pulse"></span> {stepLabel}</div>
          {deliveryDone ? <><div className="track-status-eta" style={{ color: '#128c7e' }}>✓</div><div className="track-status-detail">Sua entrega foi concluída com sucesso.</div></> : location && distanceKm !== null && etaMinutes !== null ? <><div className="track-status-eta">{etaMinutes}<span className="u">min</span></div><div className="track-status-detail">{vehicleName} está a {distanceKm.toFixed(1).replace('.', ',')} km de você.</div></> : <><div className="track-status-eta" style={{ fontSize: 25 }}>GPS</div><div className="track-status-detail">Aguardando o motorista compartilhar a localização.</div></>}
        </div>
        <div className="track-map-wrap"><div ref={mapContainerRef} id="public-track-map"></div>{!location && !deliveryDone && <div style={{ position: 'absolute', left: 16, right: 16, bottom: 16, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,.95)', fontSize: 12, color: 'var(--text-2)', boxShadow: '0 4px 12px rgba(0,0,0,.12)' }}>A posição aparecerá automaticamente quando o motorista autorizar o GPS.</div>}</div>
        <div className="track-progress"><div className="track-progress-title">Status da entrega</div><div className="track-steps"><div className="track-step done"><div className="dot"></div><div className="lbl">Rota<br />preparada</div><div className="time">{formatTime(route.date)}</div></div><div className={`track-step ${route.status === 'em_andamento' ? 'done current' : ''}`}><div className="dot"></div><div className="lbl">Saiu para<br />entrega</div><div className="time">{formatTime(route.startedAt)}</div></div><div className={`track-step ${location ? 'done current' : ''}`}><div className="dot"></div><div className="lbl">Localização<br />ao vivo</div><div className="time">{formatTime(location?.updatedAt)}</div></div><div className={`track-step ${deliveryDone ? 'done' : ''}`}><div className="dot"></div><div className="lbl">Entregue</div><div className="time">{formatTime(activeStop.completedAt)}</div></div></div></div>
        <div className="track-driver-card"><div className="track-driver-avatar">{driverName.split(' ').map((name) => name[0]).join('').slice(0, 2).toUpperCase()}</div><div className="who"><div className="name">{driverName}</div><div className="role">{location ? `GPS atualizado às ${formatTime(location.updatedAt)}` : 'Localização ainda não compartilhada'}</div><div className="veh">{vehicleName} · {route.vehicle?.plate || '—'}</div></div></div>
        <div className="track-order-details"><h4>Detalhes da entrega</h4><div className="track-detail-row"><span className="k">Destinatário</span><span className="v">{activeStop.deliveryPoint.title}</span></div><div className="track-detail-row"><span className="k">Endereço</span><span className="v">{activeStop.deliveryPoint.address}</span></div><div className="track-detail-row"><span className="k">Janela de horário</span><span className="v">{activeStop.timeWindowStart && activeStop.timeWindowEnd ? `${activeStop.timeWindowStart} - ${activeStop.timeWindowEnd}` : 'Qualquer horário'}</span></div></div>
        <div className="track-footer"><div className="brand-line">Rastreamento em tempo real por <b>OtimizaRota</b></div><div>Atualização automática a cada 15 segundos</div></div>
      </div>
    </div>
  );
}
